/**
 * Playback of the other players' voices on the shared AudioContext:
 *
 *   remote stream → MediaStreamAudioSourceNode ─┬→ AnalyserNode (speaking marks)
 *                                               └→ player GainNode → voice master GainNode
 *                                                                   → master output chain
 *
 * Web Audio rather than <audio> elements, because iOS ignores HTMLMediaElement.volume:
 * per-player and master volumes only work as gain nodes. Every remote stream is still
 * attached to a muted, playing <audio> element, because Chrome delivers a remote WebRTC
 * stream to Web Audio only while a media element plays it (silence otherwise).
 *
 * Safety net: if the analyser stays silent while the RTP receiver reports a clearly
 * audible level (seen on some WebKit builds), that voice falls back to playing through its
 * <audio> element (unmuted; the volume then works everywhere except iOS).
 */
import type { AudioHost } from '../audio/context';
import { rmsDb, volumeToGain } from '../audio/volume';
import type { RemoteAudio } from './media';

/** Gain of the voices at full volume (the output chain keeps several loud voices in check). */
const VOICE_LEVEL = 1.2;
const FFT_SIZE = 1024;
/** Level checks between two health checks (level() runs ~10 times a second). */
const HEALTH_CHECK_EVERY = 10;
/** Health checks in a row that must look wrong before falling back to the element. */
const HEALTH_STRIKES = 3;
/** RTP level (0..1) that is clearly audible, and an analyser level that is clearly silent. */
const AUDIBLE_RTP_LEVEL = 0.02;
const SILENT_DB = -70;

export class VoiceOutput {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private enabled = false;
  private volume = 1;
  private releaseHold: (() => void) | null = null;
  private readonly voices = new Set<RemoteVoice>();

  constructor(readonly host: AudioHost) {}

  /** Voices are audible (after enable()). Keeps the context running in the background. */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (enabled) this.releaseHold ??= this.host.hold();
    else {
      this.releaseHold?.();
      this.releaseHold = null;
    }
    this.apply();
  }

  setVolume(volume: number): void {
    this.volume = volume;
    this.apply();
  }

  get playing(): boolean {
    return this.enabled;
  }

  /** Master gain the element fallback multiplies in. */
  get masterGain(): number {
    return this.enabled ? volumeToGain(this.volume) * VOICE_LEVEL : 0;
  }

  /** The voice master on the current context, built on first use; null without a context. */
  bus(): GainNode | null {
    const ctx = this.host.context;
    const output = this.host.output();
    if (!ctx || !output) return null;
    if (this.ctx !== ctx || !this.master) {
      this.master?.disconnect();
      this.ctx = ctx;
      this.master = ctx.createGain();
      this.master.gain.value = this.masterGain;
      this.master.connect(output);
    }
    return this.master;
  }

  attach(
    track: MediaStreamTrack,
    stream: MediaStream | null,
    receiver: RTCRtpReceiver | null,
  ): RemoteAudio {
    const voice = new RemoteVoice(this, stream ?? new MediaStream([track]), receiver);
    this.voices.add(voice);
    voice.update();
    return voice;
  }

  forget(voice: RemoteVoice): void {
    this.voices.delete(voice);
  }

  dispose(): void {
    for (const voice of [...this.voices]) voice.dispose();
    this.master?.disconnect();
    this.master = null;
    this.ctx = null;
    this.releaseHold?.();
    this.releaseHold = null;
  }

  private apply(): void {
    const master = this.master;
    if (master && this.ctx)
      master.gain.setTargetAtTime(this.masterGain, this.ctx.currentTime, 0.03);
    for (const voice of this.voices) voice.update();
  }
}

class RemoteVoice implements RemoteAudio {
  private readonly element: HTMLAudioElement | null;
  private ctx: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private analyser: AnalyserNode | null = null;
  private gain: GainNode | null = null;
  private samples: Float32Array<ArrayBuffer> | null = null;
  private volume = 1;
  private viaElement = false;
  private checks = 0;
  private strikes = 0;
  private disposed = false;

  constructor(
    private readonly output: VoiceOutput,
    private readonly stream: MediaStream,
    private readonly receiver: RTCRtpReceiver | null,
  ) {
    this.element = playMuted(stream);
  }

  setVolume(volume: number): void {
    this.volume = volume;
    this.update();
  }

  level(): number {
    if (this.disposed) return Number.NEGATIVE_INFINITY;
    this.ensureGraph();
    if (this.viaElement) return this.rtpLevelDb();
    const analyser = this.analyser;
    const samples = this.samples;
    if (!analyser || !samples) return Number.NEGATIVE_INFINITY;
    analyser.getFloatTimeDomainData(samples);
    const db = rmsDb(samples);
    this.checkHealth(db);
    return db;
  }

  /** Builds the graph once the context exists and applies volumes. */
  update(): void {
    if (this.disposed) return;
    this.ensureGraph();
    if (this.viaElement) {
      if (this.element) {
        this.element.muted = this.output.host.silent || !this.output.playing;
        this.element.volume = Math.min(1, volumeToGain(this.volume) * this.output.masterGain);
      }
      return;
    }
    if (this.gain && this.ctx) {
      this.gain.gain.setTargetAtTime(volumeToGain(this.volume), this.ctx.currentTime, 0.03);
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.output.forget(this);
    this.disconnect();
    if (this.element) {
      this.element.pause();
      this.element.srcObject = null;
    }
  }

  private ensureGraph(): void {
    const ctx = this.output.host.context;
    if (!ctx || ctx === this.ctx) return;
    const bus = this.output.bus();
    if (!bus) return;
    this.disconnect();
    try {
      this.source = ctx.createMediaStreamSource(this.stream);
    } catch {
      return;
    }
    this.ctx = ctx;
    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = FFT_SIZE;
    this.samples = new Float32Array(FFT_SIZE);
    this.gain = ctx.createGain();
    this.gain.gain.value = this.viaElement ? 0 : volumeToGain(this.volume);
    this.source.connect(this.analyser);
    this.source.connect(this.gain);
    this.gain.connect(bus);
  }

  private disconnect(): void {
    this.source?.disconnect();
    this.analyser?.disconnect();
    this.gain?.disconnect();
    this.source = null;
    this.analyser = null;
    this.gain = null;
    this.ctx = null;
  }

  /** Detects "Web Audio hears nothing although audio arrives" and switches to the element. */
  private checkHealth(db: number): void {
    this.checks += 1;
    if (this.checks % HEALTH_CHECK_EVERY !== 0 || !this.element) return;
    if (this.ctx?.state !== 'running') {
      this.strikes = 0;
      return;
    }
    const rtp = this.rtpLevel();
    if (rtp !== null && rtp > AUDIBLE_RTP_LEVEL && db < SILENT_DB) this.strikes += 1;
    else this.strikes = 0;
    if (this.strikes >= HEALTH_STRIKES) {
      this.viaElement = true;
      if (this.gain && this.ctx) this.gain.gain.setValueAtTime(0, this.ctx.currentTime);
      this.update();
    }
  }

  /** The level the receiver reports for the last packets (0..1), if the browser tells. */
  private rtpLevel(): number | null {
    try {
      const sources = this.receiver?.getSynchronizationSources?.();
      const level = sources?.[0]?.audioLevel;
      return typeof level === 'number' ? level : null;
    } catch {
      return null;
    }
  }

  private rtpLevelDb(): number {
    const level = this.rtpLevel();
    return level !== null && level > 0 ? 20 * Math.log10(level) : Number.NEGATIVE_INFINITY;
  }
}

/** A muted <audio> element playing the stream (see the file comment); null outside a browser. */
function playMuted(stream: MediaStream): HTMLAudioElement | null {
  if (typeof Audio === 'undefined') return null;
  try {
    const element = new Audio();
    element.muted = true;
    element.autoplay = true;
    element.setAttribute('playsinline', '');
    element.srcObject = stream;
    element.play().catch(() => {
      // Muted playback is always allowed; a failure only means no Chrome workaround.
    });
    return element;
  } catch {
    return null;
  }
}
